// @flow @format

import { combineEpics, ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { of, EMPTY } from 'rxjs';

import type { ActionType } from '../utils';
import type { ObservableAction } from './types';
import { fetchAgreementBegin, fetchAgreementFailure } from '../reducer/term';
import { fetchEntitiesBegin, fetchEntitiesFailure } from '../reducer/entities';
import {
  fetchProgressBegin,
  fetchProgressFailure,
  uploadProgressBegin,
  uploadProgressFailure,
} from '../reducer/progresses';
import { fetchRankingsBegin, fetchRankingsFailure } from '../reducer/ranking';
import { fetchUserConfigBegin, fetchUserConfigFailure } from '../reducer/config';
import { finishActivityBegin, finishActivityFailure } from '../reducer/activity';
import { retryAction, showConnectionError, showError } from '../reducer/meta';
import { signinBegin, signinFailure } from '../reducer/auth';

const beginActionsMap = {
  [signinFailure.type]: ({ payload: { originalPayload } }) => signinBegin(originalPayload),
  [fetchAgreementFailure.type]: ({ payload: { originalPayload } }) => fetchAgreementBegin(originalPayload),
  [finishActivityFailure.type]: ({ payload: { originalPayload, error } }) => {
    if (error.networkError) return finishActivityBegin(originalPayload);
    throw new Error('unrecoverable');
  },
  [fetchEntitiesFailure.type]: ({ payload: { originalPayload } }) => fetchEntitiesBegin(originalPayload),
  [fetchRankingsFailure.type]: ({ payload: { originalPayload } }) => fetchRankingsBegin(originalPayload),
  [fetchProgressFailure.type]: ({ payload: { originalPayload, error } }) =>
    error.retryable && fetchProgressBegin(originalPayload),
  [fetchUserConfigFailure.type]: ({ payload: { originalPayload, error } }) =>
    error.retryable && fetchUserConfigBegin(originalPayload),
  [uploadProgressFailure.type]: ({ payload: { originalPayload, error } }) =>
    error.retryable && uploadProgressBegin(originalPayload),
};

export function queueRetryableEpic(action$: ObservableAction): ObservableAction {
  return action$.pipe(
    ofType(
      signinFailure.type,
      fetchAgreementFailure.type,
      finishActivityFailure.type,
      fetchEntitiesFailure.type,
      fetchRankingsFailure.type,
      fetchProgressFailure.type,
      fetchUserConfigFailure.type,
      uploadProgressFailure.type
    ),
    mergeMap(action => {
      try {
        const beginAction = beginActionsMap[action.type](action);
        return beginAction ? of(showConnectionError(beginAction)) : EMPTY;
      } catch (_e) {
        return of(showError({ type: 'UNRECOVERABLE_ERROR', props: {} }));
      }
    })
  );
}

export function retryEpic(action$: ObservableAction): ObservableAction {
  return action$.pipe(
    ofType<ActionType<typeof retryAction>>(retryAction.type),
    map(({ payload }) => payload)
  );
}

export default combineEpics(queueRetryableEpic, retryEpic);
